import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X } from "lucide-react";

const EMPTY = {
  title: "",
  description: "",
  image_url: "",
  prize: "",
  ticket_price: 10,
  total_tickets: 100,
  draw_date: "",
};

export default function AdminRaffles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/raffles");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      title: r.title, description: r.description, image_url: r.image_url, prize: r.prize,
      ticket_price: r.ticket_price, total_tickets: r.total_tickets, draw_date: r.draw_date,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, ticket_price: parseFloat(form.ticket_price), total_tickets: parseInt(form.total_tickets) };
      if (editing) await api.put(`/raffles/${editing}`, payload);
      else await api.post("/raffles", payload);
      toast.success(editing ? "Raffle updated" : "Raffle created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this raffle?")) return;
    await api.delete(`/raffles/${id}`);
    toast.success("Raffle removed");
    load();
  };

  return (
    <div data-testid="admin-raffles">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">Fixture List</div>
          <h1 className="font-display text-4xl text-[#F2EBDD] mt-1">Manage Raffles</h1>
        </div>
        <button data-testid="new-raffle-btn" onClick={openNew} className="btn-primary">
          <Plus size={16} /> New Raffle
        </button>
      </div>

      <div className="mt-8 border border-[#332D2B] bg-[#1C1816]">
        <table className="w-full">
          <thead className="border-b border-[#332D2B]">
            <tr className="text-left font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">
              <th className="p-4">Title</th>
              <th className="p-4">Prize</th>
              <th className="p-4">Price</th>
              <th className="p-4">Sold</th>
              <th className="p-4">Draws</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-[#332D2B] hover:bg-[#2A2320]">
                <td className="p-4 text-[#F2EBDD]">{r.title}</td>
                <td className="p-4 text-[#A8A39D] text-sm">{r.prize}</td>
                <td className="p-4 text-[#C8A96A]">£{r.ticket_price}</td>
                <td className="p-4 text-[#A8A39D]">{r.tickets_sold}/{r.total_tickets}</td>
                <td className="p-4 text-[#A8A39D] text-sm">{r.draw_date}</td>
                <td className="p-4">
                  <span className="font-mono-retro text-[10px] tracking-[0.25em] uppercase text-[#C8A96A] border border-[#C8A96A] px-2 py-1">{r.status}</span>
                </td>
                <td className="p-4 text-right space-x-2">
                  <button data-testid={`edit-raffle-${r.id}`} onClick={() => openEdit(r)} className="text-[#A8A39D] hover:text-[#C8A96A] inline-flex items-center gap-1 text-sm"><Edit3 size={14} /> Edit</button>
                  <button data-testid={`delete-raffle-${r.id}`} onClick={() => remove(r.id)} className="text-[#A8A39D] hover:text-[#8B4530] inline-flex items-center gap-1 text-sm"><Trash2 size={14} /> Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#A8A39D]">No raffles yet. Create your first.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#0F0C0B]/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="border-2 border-[#332D2B] bg-[#1C1816] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#332D2B]">
              <div className="font-display text-2xl text-[#F2EBDD]">{editing ? "Edit Raffle" : "New Raffle"}</div>
              <button onClick={() => setOpen(false)} className="text-[#A8A39D] hover:text-[#F2EBDD]"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="fz-label">Title</label>
                <input data-testid="raffle-form-title" required className="fz-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="fz-label">Description</label>
                <textarea data-testid="raffle-form-description" rows={3} className="fz-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="fz-label">Prize</label>
                <input data-testid="raffle-form-prize" required className="fz-input" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} />
              </div>
              <div>
                <label className="fz-label">Image URL</label>
                <input data-testid="raffle-form-image" required className="fz-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="fz-label">Ticket Price (£)</label>
                  <input data-testid="raffle-form-price" type="number" step="0.01" required className="fz-input" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Total Tickets</label>
                  <input data-testid="raffle-form-total" type="number" required className="fz-input" value={form.total_tickets} onChange={(e) => setForm({ ...form, total_tickets: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Draw Date</label>
                  <input data-testid="raffle-form-date" type="date" required className="fz-input" value={form.draw_date} onChange={(e) => setForm({ ...form, draw_date: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3 pt-3">
                <button data-testid="raffle-form-save" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Save Raffle"}</button>
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
